import React from 'react';
import b_ from 'b_';

import {
    useAudienceCategories,
    useAudienceInterests,
    useAudienceRequest,
    useUpdateAudienceRequest
} from 'client/common/hooks/audience';
import {useBoxType} from 'client/common/hooks/box';
import {useCanEditAudience} from 'client/common/hooks/edit-mode';

import {Spin} from 'client/common/components/lego';

import {
    AudienceCategoryInfo,
    AudienceInterestInfo,
    AudienceRequest,
    SurveySlug
} from 'client/common/types';

import {useSurveySlug} from 'client/common/contexts/slug';
import {AudienceTab} from 'client/common/components/survey-settings/types';
import AudienceManual from 'client/common/components/audience-manual';

import AudienceTargetingChoices from './audience-targeting-choices/audience-targeting-choices';

import './audience-settings.css';

const b = b_.with('audience-settings');

interface AudienceSettingsProps {
    slug: SurveySlug;
    request: AudienceRequest;
    tab?: AudienceTab;
    categories: AudienceCategoryInfo[];
    interests: AudienceInterestInfo[];
}

function AudienceSettings(props: AudienceSettingsProps) {
    const {slug, request, interests, categories, tab} = props;

    const boxType = useBoxType(slug);
    const canEdit = useCanEditAudience();
    const onAudienceUpdate = useUpdateAudienceRequest(slug);

    return (
        <div className={b({disabled: !canEdit})}>
            {tab === AudienceTab.Request && (
                <AudienceTargetingChoices
                    boxType={boxType}
                    categories={categories}
                    disabled={!canEdit}
                    interests={interests}
                    request={request}
                    slug={slug}
                    onChange={onAudienceUpdate}
                />
            )}

            {tab === AudienceTab.Manual && (
                <AudienceManual
                    boxType={boxType}
                    categories={categories}
                    interests={interests}
                    request={request}
                    onChange={onAudienceUpdate}
                />
            )}
        </div>
    );
}

interface AudienceSettingsWrapperProps {
    tab?: AudienceTab;
}

export default function AudienceSettingsWrapper(props: AudienceSettingsWrapperProps) {
    const {tab = AudienceTab.Request} = props;

    const slug = useSurveySlug();
    const request = useAudienceRequest(slug);
    const categories = useAudienceCategories(slug);
    const interests = useAudienceInterests();

    if (
        request.status !== 'success' ||
        categories.status !== 'success' ||
        interests.status !== 'success'
    ) {
        return <Spin className={b('spin')} size='xs' view='default' progress />;
    }

    return (
        <AudienceSettings
            slug={slug}
            tab={tab}
            request={request.data}
            categories={categories.data}
            interests={interests.data}
        />
    );
}
